import { Suspense, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { APP_NAME } from '@/shared/config/env';
import { ROUTES } from '@/shared/config/routes';
import { Logo, PageLoader } from '@/shared/ui';
import { FeedbackDialog } from '@/features/site-feedback';
import { UserAvatar, useSession } from '@/entities/user';
import { useAuthActions } from '@/features/auth';
import { ThemeSwitcher } from '@/features/theme-switcher';
import { LanguageSwitcher } from '@/features/language-switcher';
import { useTranslation } from 'react-i18next';
import { NAV_ITEMS, isActiveRoute } from '../model/nav-items';
import { NavDrawer } from './nav-drawer';

/**
 * The bar is two layouts, not one that shrinks.
 *
 * Below `md` everything but the wordmark and one button is hidden and moves into the drawer;
 * from `md` up the drawer never mounts its button. Trying to fit the same row into 360px was
 * what broke it: the section links overflowed off the right edge and the app became
 * unnavigable on a phone.
 */
export function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, status, hasPermission } = useSession();
  const { logout } = useAuthActions();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const closeMenu = () => setAnchorEl(null);

  const handleLogout = async () => {
    closeMenu();
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          // Real glass: a translucent wash of the page's own background, blurred. The old bar
          // asked for a blur over nothing, so it read as plain transparent and content slid
          // under the buttons on scroll.
          backgroundColor: (theme) => alpha(theme.palette.background.default, 0.72),
          backdropFilter: 'blur(12px)',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Toolbar sx={{ gap: 1, px: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              component={RouterLink}
              to="/"
              sx={{ color: 'inherit', textDecoration: 'none', mr: { md: 2 } }}
            >
              <Logo color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700} letterSpacing={-0.3}>
                {APP_NAME}
              </Typography>
            </Stack>

            {/* Sections: pills from md up, and inside the drawer below it. */}
            <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {NAV_ITEMS.filter((item) => !item.authenticatedOnly || Boolean(user)).map((item) => {
                const active = isActiveRoute(pathname, item.to);

                return (
                  <Button
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    disableElevation
                    sx={{
                      px: 2,
                      borderRadius: 2,
                      fontWeight: active ? 600 : 500,
                      color: active ? 'primary.main' : 'text.secondary',
                      backgroundColor: (theme) =>
                        active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          active
                            ? alpha(theme.palette.primary.main, 0.18)
                            : alpha(theme.palette.text.primary, 0.06),
                      },
                    }}
                  >
                    {t(item.key)}
                  </Button>
                );
              })}
            </Stack>

            <Box sx={{ flexGrow: 1 }} />

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <LanguageSwitcher />
              <ThemeSwitcher />

              {status === 'loading' ? null : user ? (
                <Button
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  aria-label={t('common.accountMenu')}
                  sx={{
                    ml: 1,
                    pl: 0.5,
                    // Below lg the label is hidden, so the button is a circle around the
                    // avatar; the start-icon margin has to go with the label or it leaves a
                    // gap on the right of nothing.
                    pr: { md: 0.5, lg: 1.5 },
                    borderRadius: 5,
                    color: 'text.primary',
                    textTransform: 'none',
                    '& .MuiButton-startIcon': { mr: { md: 0, lg: 1 }, ml: 0 },
                  }}
                  startIcon={<UserAvatar user={user} size={30} />}
                >
                  <Box
                    component="span"
                    sx={{
                      display: { md: 'none', lg: 'inline' },
                      maxWidth: 140,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {user.displayName ?? user.email}
                  </Box>
                </Button>
              ) : (
                <>
                  <Button component={RouterLink} to="/login" color="inherit" sx={{ ml: 1 }}>
                    {t('nav.signIn')}
                  </Button>
                  <Button component={RouterLink} to="/register" variant="contained">
                    {t('nav.signUp')}
                  </Button>
                </>
              )}
            </Stack>

            <IconButton
              edge="end"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('nav.menu')}
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Opened by the avatar, which only exists from md up: on a phone the same choices
          live in the drawer instead. */}
      {user && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <MenuItem disabled sx={{ opacity: '1 !important' }}>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              closeMenu();
              navigate('/profile');
            }}
          >
            {t('nav.profile')}
          </MenuItem>
          {/* Administration is not a nav item: it is rare, and a link in the main bar that
              most accounts can never open is clutter with a lock on it. */}
          {hasPermission('users:read') && (
            <MenuItem
              onClick={() => {
                closeMenu();
                navigate(ROUTES.admin);
              }}
            >
              {t('nav.administration')}
            </MenuItem>
          )}
          <MenuItem onClick={handleLogout}>{t('nav.signOut')}</MenuItem>
        </Menu>
      )}

      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSignOut={() => void handleLogout()}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, flexGrow: 1 }}>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </Container>

      {/* The way to say something about the product itself, on every page — Epic 24. The
          footer is where people look for it, and it costs one line there. */}
      <Box component="footer" sx={{ py: 2, textAlign: 'center' }}>
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
          <Typography variant="caption" sx={{ opacity: 0.5 }}>
            {APP_NAME} — Phase 1 MVP
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.5 }}>
            ·
          </Typography>
          <Button size="small" onClick={() => setFeedbackOpen(true)} sx={{ py: 0 }}>
            {t('feedback.link')}
          </Button>
        </Stack>
      </Box>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Box>
  );
}
